import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconLoader2 } from "@tabler/icons-react";
import FooterDrawer from "@/components/ui/atoms/FooterDrawer";
import MDropDownDrawer, {
  DropdownItem,
} from "@/components/ui/molecules/MDropDownDrawer";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import pillboxService from "@/services/pillbox.service";
import { MedicineTimingRuleType, PillboxItem } from "@/types/pillbox";
import { Member } from "@/types/member";
import { formatMedicineTitle } from "../utils/formatMedicine";
import styles from "./AddFromPillboxDrawer.module.css";

export interface AddFromPillboxDrawerProps {
  open: boolean;
  onClose: () => void;
  loggedInMember: Member;
  managedMembers: Member[];
  /** Called after at least one item's shopping-list membership changed. */
  onAdded: () => void;
}

function getInitials(firstName?: string, lastName?: string): string {
  return `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase();
}

/**
 * "Add from pillbox" flow (Shopping List screen) — pick a member, then
 * multi-select from their active medicines + supplements (SOS and scheduled
 * together) to add/remove from the shopping list.
 *
 * Step 1 (member picker) is custom-built on FooterDrawer directly — its
 * avatar-led rows don't fit MDropDownDrawer's flat 56px text-row layout.
 * Step 2 (medicine multi-select) reuses MDropDownDrawer, whose existing
 * "selected" row styling already matches the Figma design exactly.
 */
const AddFromPillboxDrawer: React.FC<AddFromPillboxDrawerProps> = ({
  open,
  onClose,
  loggedInMember,
  managedMembers,
  onAdded,
}) => {
  const { t } = useTranslation();
  const { showToast } = useToast();

  const [step, setStep] = useState<"member" | "medications">("member");
  const [targetMemberId, setTargetMemberId] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<PillboxItem[]>([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [initialSelectedIds, setInitialSelectedIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // managedMembers already includes the logged-in member (same convention as
  // MProfilePicker) — do not prepend loggedInMember again, or "You" doubles up.
  const allMembers = useMemo(
    () =>
      [...managedMembers].sort((a, b) => {
        if (a.id === loggedInMember.id) return -1;
        if (b.id === loggedInMember.id) return 1;
        return (a.first_name || "").localeCompare(b.first_name || "");
      }),
    [loggedInMember, managedMembers]
  );

  const memberName = (memberId: string): string => {
    if (memberId === loggedInMember.id) return t("pillbox.shoppingList.you");
    const match = allMembers.find((m) => m.id === memberId);
    return (
      match?.nickname ||
      match?.first_name ||
      t("pillbox.shoppingList.familyMember")
    );
  };

  const reset = () => {
    setStep("member");
    setTargetMemberId(null);
    setCandidates([]);
    setInitialSelectedIds([]);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  // Stays on the member-picker step throughout the fetch — only switches to
  // the medications step once we know there's something to show. Switching
  // step eagerly (then back on empty/error) unmounted/remounted the drawer,
  // which looked like the same drawer re-opening.
  const handleSelectMember = async (memberId: string) => {
    setTargetMemberId(memberId);
    setLoadingCandidates(true);
    try {
      const results = await pillboxService.getLedger(memberId, "ACTIVE");
      if (results.length === 0) {
        showToast({
          variant: "info",
          message: t("pillbox.shoppingList.noMedicationsFound"),
        });
        return;
      }
      setCandidates(results);
      setInitialSelectedIds(
        results.filter((item) => item.inShoppingList).map((item) => item.id)
      );
      setStep("medications");
    } catch {
      showToast({
        variant: "error",
        message: t("pillbox.shoppingList.loadMedicinesError"),
      });
    } finally {
      setLoadingCandidates(false);
    }
  };

  const medicineOptions: DropdownItem[] = useMemo(
    () =>
      candidates.map((item) => ({
        value: item.id,
        label: (
          <span className={styles.medicineLabel}>
            <span className={styles.medicineName}>
              {formatMedicineTitle(item)}
            </span>
            {item.timingRuleType === MedicineTimingRuleType.CONDITIONAL && (
              <span className={styles.sosBadge}>{t("pillbox.sos")}</span>
            )}
          </span>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [candidates]
  );

  const handleDone = async (selected: string[]) => {
    setSubmitting(true);
    try {
      const initiallyChecked = new Set(initialSelectedIds);
      const nowChecked = new Set(selected);
      const changed = candidates.filter(
        (item) => initiallyChecked.has(item.id) !== nowChecked.has(item.id)
      );
      await Promise.all(
        changed.map((item) =>
          pillboxService.toggleShoppingList(
            item.memberId,
            item.itemType,
            item.id,
            nowChecked.has(item.id)
          )
        )
      );
      if (changed.length > 0) onAdded();
      handleClose();
    } catch {
      showToast({
        variant: "error",
        message: t("pillbox.shoppingList.addFromPillboxError"),
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (step === "member") {
    return (
      <FooterDrawer
        open={open}
        onClose={handleClose}
        title={t("pillbox.shoppingList.selectMember")}
        showClose
      >
        <p className={styles.subtitle}>
          {t("pillbox.shoppingList.selectMemberSubtitle")}
        </p>
        <div className={styles.memberList}>
          {allMembers.map((member) => {
            const isSelf = member.id === loggedInMember.id;
            const isLoadingThis =
              loadingCandidates && targetMemberId === member.id;
            return (
              <button
                type="button"
                key={member.id}
                className={styles.memberRow}
                onClick={() => handleSelectMember(member.id)}
                disabled={loadingCandidates}
              >
                <span
                  className={isSelf ? styles.avatarSelf : styles.avatarOther}
                >
                  {member.avatar ? (
                    <img
                      src={member.avatar}
                      alt=""
                      className={styles.avatarImg}
                    />
                  ) : (
                    getInitials(member.first_name, member.last_name)
                  )}
                </span>
                <span className={styles.memberRowName}>
                  {isSelf
                    ? t("pillbox.shoppingList.you")
                    : member.nickname || member.first_name}
                </span>
                {isLoadingThis && (
                  <IconLoader2 size={20} className={styles.rowSpinner} />
                )}
              </button>
            );
          })}
        </div>
      </FooterDrawer>
    );
  }

  return (
    <MDropDownDrawer
      open={open}
      onClose={handleClose}
      title={
        targetMemberId === loggedInMember.id
          ? t("pillbox.shoppingList.yourMedications")
          : t("pillbox.shoppingList.membersMedications", {
              member: targetMemberId ? memberName(targetMemberId) : "",
            })
      }
      items={medicineOptions}
      variant="multi"
      selectedValues={initialSelectedIds}
      onSelect={() => {}}
      onDone={handleDone}
      doneLoading={submitting}
      showClose
    />
  );
};

export default AddFromPillboxDrawer;
